import { cn } from '@/lib/utils';
import { isSharePath, useBrowserPathname } from '@/lib/share';
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { router } from 'router';

import { useAuth, useChatSession, useConfig } from '@chainlit/react-client';
import type {
  ICommand,
  IPageInfo,
  IThread
} from '@chainlit/react-client';

import ChatSettingsModal from './components/ChatSettings';
import UserSettingsModal from './components/SettingsModal';
import { WizardModal } from './components/WizardModal';
import { ThemeProvider } from './components/ThemeProvider';
import { Loader } from '@/components/Loader';
import { Toaster } from '@/components/ui/sonner';

import { userEnvState, profileState } from 'state/user';
import { starterCardsState } from 'state/chat';
import { threadHistoryState, commandsState } from '@chainlit/react-client';
import { useSetRecoilState } from 'recoil';

declare global {
  interface Window {
    cl_shadowRootElement?: HTMLDivElement;
    transports?: string[];
    theme?: {
      light: Record<string, string>;
      dark: Record<string, string>;
    };
  }
}

/**
 * Backoff schedule for /api/bootstrap, in milliseconds between attempts.
 *
 * Sized to outlast a Render instance swap: the proxy answers 502 for a few
 * seconds while the new deploy takes over. Eight attempts spanning ~31s cover
 * that comfortably without leaving a user staring at a spinner for minutes when
 * the backend is genuinely down.
 */
const BOOTSTRAP_RETRY_DELAYS_MS = [500, 1000, 2000, 4000, 8000, 8000, 8000];
const DEFAULT_INTERFACE_SCALE = 0.95;

interface BootstrapProfile {
  wizard_done?: boolean;
  [key: string]: unknown;
}

/** Shape of the /api/bootstrap payload (see datalayer.get_bootstrap_data). */
interface BootstrapData {
  profile?: BootstrapProfile;
  history?: { data: IThread[]; page_info: IPageInfo };
  commands?: ICommand[];
  cards?: unknown[];
}

function App() {
  const { config } = useConfig();

  const { isAuthenticated, data, isReady, user } = useAuth();
  const userEnv = useRecoilValue(userEnvState);
  const { connect, chatProfile, setChatProfile, session } = useChatSession();

  const [showWizard, setShowWizard] = useState(false);
  const [bootstrapDone, setBootstrapDone] = useState<boolean | BootstrapData>(
    false
  );

  useEffect(() => {
    const scale = Number(
      localStorage.getItem('fongpt-text-scale') || DEFAULT_INTERFACE_SCALE
    );
    document.documentElement.style.setProperty(
      '--app-text-scale',
      String(
        (Number.isFinite(scale) ? scale : DEFAULT_INTERFACE_SCALE) /
          DEFAULT_INTERFACE_SCALE
      )
    );
    document.documentElement.classList.toggle(
      'high-contrast',
      localStorage.getItem('fongpt-high-contrast') === 'true'
    );
    document.documentElement.classList.toggle(
      'reduce-motion',
      localStorage.getItem('fongpt-reduce-motion') === 'true'
    );
  }, []);

  const setThreadHistory = useSetRecoilState(threadHistoryState);
  const setCommands = useSetRecoilState(commandsState);
  const setProfile = useSetRecoilState(profileState);
  const setStarterCards = useSetRecoilState(starterCardsState);

  const configLoaded = !!config;
  const pathname = useBrowserPathname();
  const onSharePage = isSharePath(pathname);

  // Optimized chatProfile lookup to happen in-render as well
  const effectiveChatProfile = chatProfile || (configLoaded && config.chatProfiles?.length
    ? (config.chatProfiles.find(p => p.default)?.name || config.chatProfiles[0].name)
    : undefined);

  const chatProfileOk = configLoaded
    ? (config.chatProfiles && config.chatProfiles.length > 0)
      ? !!effectiveChatProfile
      : true
    : false;

  // 1. Bootstrap initial data (Wizard status, History, Commands, Profile, Starter Cards)
  useEffect(() => {
    if (!isAuthenticated || !user?.identifier || bootstrapDone) {
      return;
    }

    let cancelled = false;
    const email = user.identifier;

    // Every deploy swaps the backend instance, and the proxy answers 502 for the
    // few seconds in between. `fetch` RESOLVES on a 502 — it does not throw — so
    // an unguarded `if (response.ok)` silently skips both the success path and
    // the catch, `bootstrapDone` never flips, and the app hangs on the loading
    // screen until the user reloads by hand. Retrying rides out the swap.
    const requestBootstrap = async (): Promise<BootstrapData | null> => {
      for (let attempt = 0; ; attempt++) {
        if (cancelled) return null;

        try {
          const response = await fetch('/api/bootstrap', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
          });

          if (response.ok) {
            return (await response.json()) as BootstrapData;
          }

          // 4xx means this request is wrong (or we are not authorised); retrying
          // it would just repeat the same failure. Only 5xx is worth waiting out.
          if (response.status < 500) {
            console.error(`Bootstrap failed with ${response.status}`);
            return null;
          }
        } catch (error) {
          // Network-level failure: the connection dropped mid-swap.
          console.error('Error bootstrapping app data:', error);
        }

        const delay = BOOTSTRAP_RETRY_DELAYS_MS[attempt];
        if (delay === undefined) return null;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    };

    const bootstrap = async () => {
      const data = await requestBootstrap();
      if (cancelled) return;

      // Degrade rather than hang: an empty payload still lets the socket connect
      // below (the guard there only rejects booleans), so the user gets a working
      // chat with an unprimed sidebar instead of an infinite spinner.
      if (!data) {
        setBootstrapDone({});
        return;
      }

      // 1. Set Profile & Check Wizard status
      if (data.profile) {
        setProfile(data.profile);
        const wizardDone = data.profile?.wizard_done;
        if (!wizardDone) {
          setShowWizard(true);
        }
      }

      // 2. Set Thread History
      const history = data.history;
      if (history) {
        setThreadHistory((prev) => ({
          ...prev,
          threads: history.data,
          pageInfo: history.page_info,
        }));
      }

      // 3. Set Commands
      if (data.commands) {
        setCommands(data.commands);
      }

      // 4. Set Starter Cards
      if (data.cards) {
        setStarterCards(data.cards);
      }

      // Store profile for the connection
      setBootstrapDone(data);
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.identifier, bootstrapDone, setThreadHistory, setCommands, setProfile, setStarterCards]);

  // 2. Connect to the socket ONLY after bootstrap is done
  // We pass the profile in userEnv so the backend doesn't need to re-fetch it
  useEffect(() => {
    if (onSharePage) return;
    if (!isAuthenticated || !isReady || !chatProfileOk || !bootstrapDone || typeof bootstrapDone === 'boolean') {
      return;
    }

    // Narrowed by the `typeof === 'boolean'` guard above. `profile` is absent
    // when bootstrap was degraded after a failed retry loop; nothing on the
    // backend reads it out of userEnv, so the session still starts cleanly.
    const data: BootstrapData = bootstrapDone;

    connect({
      transports: window.transports,
      userEnv: {
        ...userEnv,
        // userEnv is declared Record<string, string>; the profile object has
        // always ridden along as-is, so the cast preserves existing behaviour
        // rather than introducing it.
        profile: data.profile as unknown as string
      }
    });
  }, [userEnv, isAuthenticated, connect, isReady, chatProfileOk, bootstrapDone, onSharePage]);

  useEffect(() => {
    if (
      !configLoaded ||
      !config ||
      !config.chatProfiles?.length ||
      chatProfile
    ) {
      return;
    }

    const defaultChatProfile = config.chatProfiles.find(
      (profile) => profile.default
    );

    if (defaultChatProfile) {
      setChatProfile(defaultChatProfile.name);
    } else {
      setChatProfile(config.chatProfiles[0].name);
    }
  }, [configLoaded, config, chatProfile, setChatProfile]);

  if (!configLoaded && isAuthenticated && !onSharePage) return null;

  // Share pages are public: don't wait for auth bootstrap / websocket.
  const appReady =
    onSharePage ||
    (isReady && (isAuthenticated ? (bootstrapDone && !!session?.socket) : true));

  return (
    <ThemeProvider
      storageKey="vite-ui-theme"
      defaultTheme={data?.default_theme}
    >
      <Toaster richColors className="toast" position="top-right" />

      {!onSharePage && (
        <>
          <ChatSettingsModal />
          <UserSettingsModal />
          <WizardModal
            open={showWizard}
            onComplete={() => {
              setShowWizard(false);
            }}
          />
        </>
      )}
      <RouterProvider router={router} />

      <div
        className={cn(
          'bg-[hsl(var(--background))] flex items-center justify-center fixed size-full p-2 top-0 z-[9999]',
          appReady && 'hidden'
        )}
      >
        <Loader className="!size-6" />
      </div>
    </ThemeProvider>
  );
}

export default App;
